import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Button,
    TextField, Divider, CircularProgress, Alert, Chip,
    Paper
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import paymentService from '../../../services/paymentService';
import { useNavigate } from 'react-router-dom';
import HomeLayout from '../../../components/layout/HomeLayout';

const DEPOSIT_AMOUNTS = [50000, 100000, 200000, 500000, 1000000];

const DepositPage = () => {
    const navigate = useNavigate();
    const [balance, setBalance] = useState(0);
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [qrData, setQrData] = useState(null); // { code, amount }
    const [backendQrUrl, setBackendQrUrl] = useState(''); // New state for backend provided URL
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [checking, setChecking] = useState(false);

    // Countdown Logic
    const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds

    useEffect(() => {
        fetchBalance();
    }, []);

    const fetchBalance = async () => {
        try {
            const res = await paymentService.getBalance();
            if (res.success) setBalance(Number(res.balance));
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateQR = async () => {
        const numAmount = Number(amount);
        if (!numAmount || numAmount < 2000) {
            setError('Số tiền nạp tối thiểu là 2,000 VNĐ');
            return;
        }

        setError('');
        setLoading(true);
        setQrData(null);
        setBackendQrUrl('');
        setSuccess(false);
        setTimeLeft(600);

        try {
            const res = await paymentService.createDeposit(numAmount);
            if (res.success) {
                setQrData({
                    code: res.code,
                    amount: numAmount
                });
                setBackendQrUrl(res.qrUrl);
            } else {
                setError(res.message || 'Không thể tạo mã nạp tiền');
            }
        } catch (err) {
            setError('Lỗi kết nối server');
        } finally {
            setLoading(false);
        }
    };

    // Manual Check Logic
    const handleCheckPayment = async () => {
        if (!qrData) return;
        setChecking(true);
        setError('');
        try {
            const res = await paymentService.checkSepayTransaction(qrData.code, qrData.amount);
            if (res?.success) {
                setSuccess(true);
                setBalance(curr => curr + qrData.amount); // Optimistic update
                fetchBalance(); // Actual sync
            } else if (res?.expired) {
                setError(res.message || 'Mã nạp tiền đã quá hạn. Vui lòng tạo mã mới!');
                setQrData(null);
            } else {
                setError(res?.message || 'Chưa nhận được thanh toán. Vui lòng thử lại sau giây lát!');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Lỗi khi kiểm tra giao dịch.');
        } finally {
            setChecking(false);
        }
    };

    const handleCancelPayment = async () => {
        if (!qrData) return;
        try {
            await paymentService.cancelDeposit(qrData.code);
        } catch (err) {
            console.error('Failed to cancel deposit', err);
        }
        setQrData(null);
        setError('');
        setSuccess(false);
    };

    // Countdown Logic
    useEffect(() => {
        let timerId;
        if (qrData && !success && !error && timeLeft > 0) {
            timerId = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && qrData && !success && !error) {
            setError('Mã nạp tiền đã quá hạn 10 phút. Vui lòng tạo mã mới!');
            setQrData(null);
        }
        return () => clearInterval(timerId);
    }, [qrData, success, error, timeLeft]);

    return (
        <HomeLayout>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, display: 'flex', alignItems: 'center', color: '#fff' }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 40, mr: 2, color: '#60a5fa' }} />
                    Nạp Tiền Vào Ví
                </Typography>

                <Grid container spacing={4}>
                    {/* Left Side: Input Amount */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', bgcolor: '#1e293b', color: 'white' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Số Dư Hiện Tại: <strong style={{ color: '#4ade80' }}>{balance.toLocaleString()} VNĐ</strong></Typography>
                                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

                                <Typography gutterBottom sx={{ color: '#cbd5e1' }}>Chọn số tiền nạp:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                    {DEPOSIT_AMOUNTS.map(amt => (
                                        <Chip
                                            key={amt}
                                            label={amt.toLocaleString()}
                                            onClick={() => setAmount(amt)}
                                            sx={{
                                                bgcolor: amount === amt ? '#3b82f6' : 'rgba(255,255,255,0.05)',
                                                color: 'white',
                                                border: '1px solid',
                                                borderColor: amount === amt ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                                                '&:hover': { bgcolor: amount === amt ? '#2563eb' : 'rgba(255,255,255,0.1)' }
                                            }}
                                            clickable
                                        />
                                    ))}
                                </Box>

                                <Typography gutterBottom sx={{ color: '#cbd5e1', mt: 2 }}>Hoặc nhập số tiền khác:</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Nhập số tiền (VNĐ)"
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    InputProps={{
                                        inputProps: { min: 2000 },
                                        style: { color: 'white' }
                                    }}
                                    sx={{
                                        mb: 3,
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                            '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
                                            bgcolor: 'rgba(0,0,0,0.2)'
                                        },
                                        '& .MuiInputLabel-root': { color: '#94a3b8' }
                                    }}
                                />

                                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    onClick={handleCreateQR}
                                    disabled={loading || !amount || success}
                                    sx={{
                                        bgcolor: '#3b82f6',
                                        fontWeight: 'bold',
                                        py: 1.5,
                                        '&:hover': { bgcolor: '#2563eb' },
                                        '&:disabled': { bgcolor: 'rgba(59, 130, 246, 0.5)' }
                                    }}
                                >
                                    {loading ? 'Đang tạo mã...' : 'Tạo Mã QR Chuyển Khoản'}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Side: QR Display */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                {!qrData ? (
                                    <Box sx={{ color: '#94a3b8', py: 4 }}>
                                        <Typography>Vui lòng nhập số tiền và bấm "Tạo Mã QR"</Typography>
                                    </Box>
                                ) : success ? (
                                    <Box>
                                        <Alert severity="success" sx={{ mb: 3, bgcolor: 'rgba(74, 222, 128, 0.1)', color: '#4ade80' }}>
                                            <Typography variant="h6">Nạp tiền thành công!</Typography>
                                            <Typography>Số dư mới: {(balance).toLocaleString()} VNĐ</Typography>
                                        </Alert>
                                        <Button variant="outlined" onClick={() => navigate('/home')} sx={{ color: 'white', borderColor: 'white', mr: 2 }}>
                                            Về Trang Chủ
                                        </Button>
                                        <Button variant="contained" onClick={() => { setQrData(null); setAmount(''); setSuccess(false); }} sx={{ bgcolor: '#3b82f6' }}>
                                            Nạp Tiếp
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2 }}>Quét Mã Để Thanh Toán</Typography>
                                        <Paper elevation={3} sx={{ p: 1, display: 'inline-block', mb: 2, bgcolor: 'white' }}>
                                            <img
                                                src={backendQrUrl}
                                                alt="VietQR"
                                                style={{ width: '100%', maxWidth: 250, display: 'block' }}
                                            />
                                        </Paper>

                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Nội dung chuyển khoản (Bắt buộc):</Typography>
                                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#60a5fa', bgcolor: 'rgba(59, 130, 246, 0.1)', p: 1, borderRadius: 1, display: 'inline-block', mt: 1 }}>
                                                {qrData.code}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: '#f87171', fontWeight: 'bold', mt: 1 }}>
                                                Mã QR hết hạn sau: {Math.floor(timeLeft / 60).toString().padStart(2, '0')}:{(timeLeft % 60).toString().padStart(2, '0')}
                                            </Typography>

                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={handleCheckPayment}
                                                disabled={checking}
                                                sx={{
                                                    mt: 1,
                                                    bgcolor: '#10b981',
                                                    '&:hover': { bgcolor: '#059669' },
                                                    fontWeight: 'bold',
                                                    py: 1.5
                                                }}
                                            >
                                                {checking ? <><CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> Đang kiểm tra...</> : 'Tôi Đã Thanh Toán'}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={handleCancelPayment}
                                                disabled={checking}
                                                sx={{
                                                    mt: 1,
                                                    color: '#f87171',
                                                    borderColor: '#f87171',
                                                    '&:hover': { bgcolor: 'rgba(248, 113, 113, 0.1)', borderColor: '#ef4444' }
                                                }}
                                            >
                                                Hủy Giao Dịch
                                            </Button>
                                        </Box>

                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </HomeLayout>
    );
};

export default DepositPage;
